import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

const milestones = [
  { year: '2020', title: '平台创立', desc: '91免费版正式上线，首批收录500款精品免费软件，获得用户广泛好评。' },
  { year: '2021', title: '快速成长', desc: '软件库突破1500款，月活用户突破50万，上线软件分类导航系统。' },
  { year: '2022', title: '品牌升级', desc: '完成品牌全面升级，推出安全检测机制，确保所有软件纯净无毒无捆绑。' },
  { year: '2023', title: '生态拓展', desc: '软件库突破2500款，上线文章资讯板块，为用户提供使用教程和软件推荐。' },
  { year: '2024', title: '行业领先', desc: '累计用户突破500万，成为中文互联网最受欢迎的免费软件下载平台之一。' },
];

const testimonials = [
  {
    name: '张先生',
    role: '自由职业者',
    content: '91免费版真的太方便了！以前找软件要在各个网站到处搜，现在一个平台全都搞定。而且软件都是纯净无捆绑的，用着特别放心。',
    rating: 5,
  },
  {
    name: '李女士',
    role: '大学生',
    content: '作为学生党预算有限，91免费版帮我找到了好多免费又好用的学习工具。使用教程也特别详细，新手也能轻松上手。',
    rating: 5,
  },
  {
    name: '王工',
    role: '软件开发工程师',
    content: '开发工具分类做得很好，VS Code、Git这些必备工具都有收录，版本更新也很及时。推荐给了很多同事用。',
    rating: 5,
  },
  {
    name: '陈同学',
    role: '高中生',
    content: '电脑配置不高，91免费版推荐的轻量级工具特别适合我。安装教程一步步很清晰，完全不会搞错。',
    rating: 4,
  },
  {
    name: '赵老师',
    role: '中学教师',
    content: '教学用的软件在办公分类里都能找到，WPS、PDF工具都很实用。客服响应也很快，遇到问题很快就能解决。',
    rating: 5,
  },
  {
    name: '刘经理',
    role: '创业公司主管',
    content: '团队用的免费协作工具都是在91免费版上找的，帮我们节省了不少软件采购成本。真心推荐给所有中小团队。',
    rating: 5,
  },
];

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.91free.com' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.91free.com/about' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于91免费版',
    description: '91免费版是值得信赖的免费软件下载平台，致力于为用户提供安全、纯净、高效的软件下载服务。',
    url: 'https://www.91free.com/about',
    about: {
      '@type': 'Organization',
      name: '91免费版',
      foundingDate: '2020',
      description: '91免费版是一个集免费软件下载、使用教程、资讯推荐为一体的综合平台。',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91免费版',
    url: 'https://www.91free.com',
    logo: 'https://www.91free.com/logo.png',
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'support@91free.com',
      availableLanguage: ['Chinese'],
    },
    sameAs: [
      'https://weibo.com/91free',
      'https://www.zhihu.com/org/91free',
    ],
  },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      return;
    }
    formData.delete('website_alt');

    try {
      const searchParams = new URLSearchParams();
      formData.forEach((value, key) => {
        searchParams.append(key, String(value));
      });

      const response = await fetch('https://readdy.ai/api/form/d9pkfjhn2trhb35gvh2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: searchParams.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError('提交失败，请稍后重试。');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试。');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍与联系方式"
        description="91免费版致力于打造安全可靠的免费软件下载平台。了解我们的品牌故事、发展历程、用户口碑与联系方式。91免费版，让每个人都能享受优质免费的软件服务。"
        keywords="91免费版,关于我们,品牌介绍,联系方式,免费软件平台,软件下载"
        canonicalUrl="https://www.91free.com/about"
        ogTitle="关于91免费版 - 品牌介绍与联系方式"
        ogDescription="了解91免费版的品牌故事、发展历程与用户口碑。"
        schemaData={aboutSchema}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16 bg-background-50 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <section className="py-12">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4">
                品牌介绍
              </span>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于91免费版
              </h1>
              <p className="text-foreground-600 max-w-2xl mx-auto text-base leading-relaxed">
                91免费版创立于2020年，是国内领先的免费软件综合服务平台。
                我们致力于打破软件付费壁垒，让每一位用户都能免费享受优质软件带来的便利与效率提升。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              <div className="relative h-72 lg:h-auto rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20tech%20office%20workspace%20with%20multiple%20screens%20displaying%20colorful%20software%20interface%2C%20warm%20indoor%20lighting%2C%20clean%20aesthetic%2C%20professional%20creative%20atmosphere%2C%20team%20collaboration%20scene%2C%20natural%20wood%20desk%2C%20indoor%20plants%2C%20editorial%20photography%20style%20with%20soft%20shadows&width=800&height=600&seq=about-brand-91&orientation=landscape"
                  alt="91免费版团队工作环境"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-5">
                  我们的使命
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  在数字化时代，软件工具是每个人提升效率、释放创造力的关键。
                  然而高额的软件费用让许多人望而却步。91免费版的诞生，就是为了改变这一现状。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  我们相信<strong>好用的软件不应该成为少数人的特权</strong>。
                  通过严格的软件筛选和安全检测，我们将真正优质、安全的免费软件带给每一位用户。
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  五年来，我们始终坚持<strong>"免费、安全、纯净"</strong>的核心原则，
                  累计服务超过500万用户，平台收录3200+款精品免费软件，涵盖八大核心分类。
                </p>
              </div>
            </div>
          </section>

          <section className="py-12 border-t border-background-200">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-700 mb-4">
                发展历程
              </span>
              <h2 className="text-3xl font-heading font-bold text-foreground-900">
                我们的成长足迹
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200 md:-translate-x-px" />
              <div className="flex flex-col gap-8">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 -translate-x-1/2 mt-1.5 z-10" />
                    <div className={`pl-10 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                      <div className="bg-white border border-background-200/70 rounded-lg p-5">
                        <span className="text-sm font-bold text-primary-500">{item.year}</span>
                        <h3 className="text-base font-semibold text-foreground-900 mt-1 mb-2">{item.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                    <div className="hidden md:block md:w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-12 border-t border-background-200">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 mb-4">
                用户口碑
              </span>
              <h2 className="text-3xl font-heading font-bold text-foreground-900">
                听听用户怎么说
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, index) => (
                <div
                  key={index}
                  className="p-6 rounded-lg bg-white border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i
                        key={star}
                        className={`text-xs ${
                          star <= t.rating
                            ? 'ri-star-fill text-yellow-500'
                            : 'ri-star-line text-foreground-300'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="py-12 border-t border-background-200">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-100 text-accent-700 mb-4">
                联系我们
              </span>
              <h2 className="text-3xl font-heading font-bold text-foreground-900 mb-3">
                期待你的声音
              </h2>
              <p className="text-foreground-600 max-w-lg mx-auto text-base">
                有任何问题、建议或合作意向，欢迎通过以下方式与我们联系。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2 flex flex-col gap-5">
                <div className="flex items-start gap-4 p-5 rounded-lg bg-white border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">support@91free.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-lg bg-white border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-600">91免费版</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-lg bg-white border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-weibo-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">官方微博</h3>
                    <p className="text-sm text-foreground-600">@91免费版</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-lg bg-white border border-background-200/70">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-600">周一至周日 9:00 - 22:00</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="bg-white border border-background-200/70 rounded-lg p-6 md:p-8">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-6">发送消息</h3>

                  {formStatus === 'success' ? (
                    <div className="text-center py-12">
                      <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-check-line text-2xl" />
                      </div>
                      <h4 className="text-lg font-semibold text-foreground-900 mb-2">消息已发送</h4>
                      <p className="text-sm text-foreground-500">感谢你的留言，我们会尽快回复。</p>
                      <button
                        className="mt-5 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                        onClick={() => setFormStatus('idle')}
                      >
                        发送新消息
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} data-readdy-form="true">
                      <div className="spam-protect">
                        <input
                          type="text"
                          name="website_alt"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            姓名 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            id="contact-name"
                            type="text"
                            name="name"
                            required
                            className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all bg-background-50"
                            placeholder="请输入姓名"
                          />
                        </div>
                        <div>
                          <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                            邮箱 <span className="text-primary-500">*</span>
                          </label>
                          <input
                            id="contact-email"
                            type="email"
                            name="email"
                            required
                            className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all bg-background-50"
                            placeholder="请输入邮箱"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          主题
                        </label>
                        <input
                          id="contact-subject"
                          type="text"
                          name="subject"
                          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all bg-background-50"
                          placeholder="请输入消息主题"
                        />
                      </div>

                      <div className="mb-5">
                        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                          留言内容 <span className="text-primary-500">*</span>
                        </label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={5}
                          className="w-full px-4 py-2.5 text-sm border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all bg-background-50 resize-none"
                          placeholder="请输入留言内容（最多500字）"
                        />
                      </div>

                      {formStatus === 'error' && (
                        <p className="text-sm text-red-500 mb-4">{formError}</p>
                      )}

                      <button
                        type="submit"
                        className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                      >
                        发送消息
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}